/* eslint-disable */
// src/pages/Login.jsx

import React, { useEffect, useMemo, useState } from "react";
import "./Login.css";

import { auth, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
} from "firebase/auth";

import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const PASSWORD_MIN = 8;
const PASSWORD_MAX = 64;

export default function Login() {
  const {
    user,
    role,
    profile,
    authResolved,
    profileResolved,
    profileLoading,
  } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [serverLockMinutes, setServerLockMinutes] = useState(null);

  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);

  const effectiveRole = useMemo(() => {
    const rawRole = role || profile?.role || null;
    const lower = String(rawRole || "").toLowerCase().trim();

    if (lower === "teacher") return "staff";
    if (lower === "manager") return "admin";

    return lower || null;
  }, [role, profile]);

  const authSettled =
    authResolved && (!user || profileResolved || !profileLoading);

  const requiresMfaSetup =
    (effectiveRole === "admin" ||
      effectiveRole === "super_admin" ||
      effectiveRole === "super-admin") &&
    profile?.mfaRequired === true &&
    profile?.mfaCompleted !== true;

  useEffect(() => {
    const handler = (e) => {
      const root = document.querySelector(".login-page");
      if (!root) return;

      root.style.setProperty("--mouse-x", `${e.clientX}px`);
      root.style.setProperty("--mouse-y", `${e.clientY}px`);
    };

    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

  async function handleEmailLogin(e) {
    e.preventDefault();

    if (loading || resetLoading || mfaLoading) return;

    setLoading(true);
    setError("");
    setMessage("");
    setMfaResolver(null);
    setMfaCode("");

    const cleanEmail = normalizeEmail(email);

    try {
      if (!cleanEmail) throw new Error("Enter your email address.");

      if (password.length < PASSWORD_MIN) {
        throw new Error(`Password must be at least ${PASSWORD_MIN} characters.`);
      }

      if (password.length > PASSWORD_MAX) {
        throw new Error(`Password cannot exceed ${PASSWORD_MAX} characters.`);
      }

      const checkLoginLock = httpsCallable(functions, "checkLoginLock");
      const lockResult = await checkLoginLock({ email: cleanEmail });

      if (lockResult?.data?.locked) {
        const mins = lockResult.data.remainingMinutes || 10;

        setServerLockMinutes(mins);
        setError(
          `Too many failed login attempts. Login temporarily locked for ${mins} minute${
            mins > 1 ? "s" : ""
          }.`
        );
        return;
      }

      await signInWithEmailAndPassword(auth, cleanEmail, password);

      const clearLoginLock = httpsCallable(functions, "clearLoginLock");
      await clearLoginLock({ email: cleanEmail }).catch(() => {});

      setServerLockMinutes(null);
    } catch (err) {
      console.error("Login failed:", err);

      if (err?.code === "auth/multi-factor-auth-required") {
        const resolver = getMultiFactorResolver(auth, err);
        setMfaResolver(resolver);
        setError("");
        setMessage("Enter the 6-digit code from your authenticator app.");
        return;
      }

      if (
        err?.code === "auth/invalid-credential" ||
        err?.code === "auth/wrong-password" ||
        err?.code === "auth/user-not-found"
      ) {
        try {
          const recordFailedLoginAttempt = httpsCallable(
            functions,
            "recordFailedLoginAttempt"
          );

          const failedResult = await recordFailedLoginAttempt({
            email: cleanEmail,
          });

          if (failedResult?.data?.locked) {
            const mins = failedResult.data.remainingMinutes || 10;

            setServerLockMinutes(mins);
            setError(
              `Too many failed login attempts. Login temporarily locked for ${mins} minute${
                mins > 1 ? "s" : ""
              }.`
            );
          } else {
            const remaining = failedResult?.data?.remainingAttempts ?? 0;

            setServerLockMinutes(null);
            setError(
              `Invalid email or password. ${remaining} attempt${
                remaining === 1 ? "" : "s"
              } remaining.`
            );
          }
        } catch (recordErr) {
          console.error("Failed to record login attempt:", recordErr);
          setError("Invalid email or password.");
        }
      } else {
        setError(err?.message || "Login failed.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaVerify(e) {
    e.preventDefault();

    if (!mfaResolver || mfaLoading) return;

    try {
      setMfaLoading(true);
      setError("");
      setMessage("");

      const cleanCode = String(mfaCode || "").trim();

      if (!cleanCode || cleanCode.length < 6) {
        throw new Error("Enter the 6-digit authenticator code.");
      }

      const hint = mfaResolver.hints?.[0];

      if (!hint?.uid) {
        throw new Error("No MFA enrolment found for this account.");
      }

      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        hint.uid,
        cleanCode
      );

      await mfaResolver.resolveSignIn(assertion);

      setMfaResolver(null);
      setMfaCode("");
      setMessage("MFA verified. Redirecting...");
    } catch (err) {
      console.error("MFA verification failed:", err);
      setError(err?.message || "Could not verify MFA code.");
    } finally {
      setMfaLoading(false);
    }
  }

  async function handleGoogleLogin() {
    if (loading || resetLoading || serverLockMinutes || mfaLoading) return;

    setLoading(true);
    setError("");
    setMessage("");
    setMfaResolver(null);
    setMfaCode("");

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });

      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error("Google sign-in failed:", err);

      if (err?.code === "auth/multi-factor-auth-required") {
        const resolver = getMultiFactorResolver(auth, err);
        setMfaResolver(resolver);
        setError("");
        setMessage("Enter the 6-digit code from your authenticator app.");
        return;
      }

      setError("Google sign-in failed.");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    if (!email) {
      setError("Enter your email first.");
      return;
    }

    try {
      setResetLoading(true);
      setError("");
      setMessage("");

      const resetFn = httpsCallable(functions, "requestParentPasswordReset");

      await resetFn({
        email: normalizeEmail(email),
      });

      setMessage("Password reset email sent. Check your inbox.");
    } catch (err) {
      console.error("Password reset failed:", err);
      setError("Failed to send reset email.");
    } finally {
      setResetLoading(false);
    }
  }

  if (authSettled && user) {
    if (requiresMfaSetup) {
      return <Navigate to="/mfa-setup" replace />;
    }

    if (effectiveRole === "super_admin" || effectiveRole === "super-admin") {
      return <Navigate to="/super-admin" replace />;
    }

    if (effectiveRole === "parent") {
      return <Navigate to="/parent/home" replace />;
    }

    if (effectiveRole === "admin") {
      if (profile?.onboardingRequired && !profile?.onboardingCompleted) {
        return <Navigate to="/school-admin-onboarding" replace />;
      }

      return <Navigate to="/admin-manager?tab=overview" replace />;
    }

    if (effectiveRole === "staff") {
      return <Navigate to="/teacher-dashboard" replace />;
    }

    return <Navigate to="/" replace />;
  }

  return (
    <div className="login-page">
      <div className={`login-card ${error ? "shake" : ""}`}>
        <div className="login-brand">
          <img
            src="/presentplus-logo.png"
            alt="PresentPlus"
            className="login-logo"
          />
          <h1 className="login-title">PresentPlus</h1>
        </div>

        <p className="login-subtitle">
          Secure access for admins, teachers, parents, and platform operators.
        </p>

        {error && !serverLockMinutes ? (
          <div className="login-error">{error}</div>
        ) : null}

        {message ? <div className="login-success">{message}</div> : null}

        {serverLockMinutes ? (
          <div className="login-lock-warning">
            Too many failed login attempts.
            <br />
            Login temporarily locked for {serverLockMinutes} minute
            {serverLockMinutes > 1 ? "s" : ""}.
          </div>
        ) : null}

        {mfaResolver ? (
          <form onSubmit={handleMfaVerify}>
            <div className="field-row">
              <label htmlFor="mfaCode">Authenticator code</label>

              <input
                id="mfaCode"
                name="mfaCode"
                type="text"
                value={mfaCode}
                placeholder="123456"
                onChange={(e) => setMfaCode(e.target.value)}
                autoComplete="one-time-code"
                required
                disabled={mfaLoading}
              />
            </div>

            <button className="primary-btn" disabled={mfaLoading}>
              {mfaLoading ? "Verifying..." : "Verify MFA"}
            </button>

            <button
              type="button"
              className="google-btn"
              onClick={() => {
                setMfaResolver(null);
                setMfaCode("");
                setMessage("");
                setError("");
              }}
              disabled={mfaLoading}
              style={{ marginTop: 12 }}
            >
              Back to sign in
            </button>
          </form>
        ) : (
          <>
            <form onSubmit={handleEmailLogin}>
              <div className="field-row">
                <label htmlFor="email">Email</label>

                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  placeholder="example@school.com"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setServerLockMinutes(null);
                  }}
                  autoComplete="email"
                  required
                  disabled={loading || resetLoading}
                />
              </div>

              <div className="field-row">
                <div className="field-label-row">
                  <label htmlFor="password">Password</label>

                  <button
                    type="button"
                    className="forgot-link"
                    onClick={handleForgotPassword}
                    disabled={resetLoading || loading}
                  >
                    {resetLoading ? "Sending..." : "Forgot password?"}
                  </button>
                </div>

                <div className="password-wrapper">
                  <input
                    id="password"
                    name="password"
                    type={showPass ? "text" : "password"}
                    value={password}
                    placeholder="Enter password"
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    minLength={PASSWORD_MIN}
                    maxLength={PASSWORD_MAX}
                    required
                    disabled={loading || resetLoading}
                  />

                  <button
                    type="button"
                    className="toggle-pass"
                    onClick={() => setShowPass(!showPass)}
                    disabled={loading || resetLoading}
                  >
                    {showPass ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>

              <button
                className="primary-btn"
                disabled={loading || resetLoading || !!serverLockMinutes}
              >
                {serverLockMinutes
                  ? `Locked for ${serverLockMinutes} minute${
                      serverLockMinutes > 1 ? "s" : ""
                    }`
                  : loading
                  ? "Authenticating..."
                  : "Sign in"}
              </button>
            </form>

            <div className="login-divider">
              <span>or continue with</span>
            </div>

            <button
              className="google-btn"
              onClick={handleGoogleLogin}
              disabled={loading || resetLoading || !!serverLockMinutes}
            >
              <span className="google-mark">G</span>
              <span>Continue with Google</span>
            </button>
          </>
        )}

        <div className="login-footer-note">
          Role-based secure access • PresentPlus Platform
        </div>
      </div>
    </div>
  );
}